import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Crown, Heart, Sparkles } from 'lucide-react'
import { showErrorToast, showSuccessToast } from '@/lib/toast'
import type { Product } from '@/types'
import { formatCurrency, stockLimitMessage } from '@/lib/utils'
import { useWishlistStore } from '@/store/stores/wishlistStore'
import { useCartStore } from '@/store/stores/cartStore'
import { Button } from '@/components/ui/Button'
import { StoreImage } from '@/store/components/StoreImage'
import { cn } from '@/lib/utils'
import {
  pickProductSize,
  availableProductSizes,
  type ProductSizePick,
} from '@/lib/productSize'
import { getSizeVariant, productVariants, variantImage } from '@/lib/productVariant'
import { productHasAudience } from '@/lib/productAudience'
import { ProductAudienceBadge } from '@/store/components/ProductAudienceBadge'
import { StarRatingDisplay } from '@/store/components/StarRating'
import { productDetailLinkState } from '@/lib/productRoute'
import { useProductGridTitleSync } from '@/store/components/ProductGridTitleSync'
import { QuantityStepper, storeEase } from '@/store/ui'

interface ProductCardProps {
  product: Product
  className?: string
  showActions?: boolean
  defaultSizePick?: ProductSizePick
  selectedSize?: string
  onSizeChange?: (size: string) => void
  sortHighlighted?: boolean
  sortHighlightToken?: number
  imagePriority?: boolean
}

export function ProductCard({
  product,
  className,
  showActions,
  defaultSizePick = 'first-in-stock',
  selectedSize,
  onSizeChange,
  sortHighlighted = false,
  sortHighlightToken,
  imagePriority = false,
}: ProductCardProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const titleMeasureRef = useRef<HTMLSpanElement>(null)
  const titleSync = useProductGridTitleSync()
  const titleLines = titleSync?.titleLines ?? 1
  const reportTitleLines = titleSync?.reportTitleLines
  const navigate = useNavigate()
  const location = useLocation()
  const addItem = useCartStore((s) => s.addItem)
  const startBuyNow = useCartStore((s) => s.startBuyNow)
  const { has, toggle } = useWishlistStore()
  const [internalSize, setInternalSize] = useState<string>(() =>
    pickProductSize(product, defaultSizePick)
  )
  const [qty, setQty] = useState(1)

  const isControlled = selectedSize !== undefined
  const size = isControlled ? selectedSize : internalSize

  const updateSize = (next: string) => {
    if (!isControlled) setInternalSize(next)
    onSizeChange?.(next)
  }

  const wished = has(product.id)
  const hasSizes = Boolean(product.sizes?.length)

  const priceInfo = useMemo(() => {
    if (hasSizes) {
      const v = product.sizes!.find((s) => s.size === size)
      return {
        price: v?.discountPrice ?? v?.price ?? product.price,
        originalPrice: v?.price ?? product.price,
        hasDiscount: Boolean(v?.discountPrice),
        stock: v?.quantity ?? 0,
      }
    }
    return {
      price: product.discountPrice ?? product.price,
      originalPrice: product.price,
      hasDiscount: Boolean(product.discountPrice),
      stock: product.quantity,
    }
  }, [product, size, hasSizes])

  const outOfStock = priceInfo.stock <= 0
  const selectedVariant = hasSizes ? getSizeVariant(product, size) : undefined
  const displayImage =
    (hasSizes ? variantImage(selectedVariant, product) : product.images[0]) ||
    product.images[0]

  useEffect(() => {
    setQty((current) => Math.min(current, Math.max(1, priceInfo.stock)))
  }, [size, priceInfo.stock])

  useEffect(() => {
    if (!hasSizes) return
    const available = availableProductSizes(product)
    if (size && available.includes(size)) return
    updateSize(pickProductSize(product, defaultSizePick))
  }, [product, hasSizes, size, defaultSizePick])

  useLayoutEffect(() => {
    const el = titleMeasureRef.current
    if (!el || !reportTitleLines) return

    const measure = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight)
      const lines: 1 | 2 =
        Number.isFinite(lineHeight) && lineHeight > 0
          ? el.scrollHeight / lineHeight > 1.05
            ? 2
            : 1
          : 1
      reportTitleLines(product.id, lines)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [product.id, product.name, reportTitleLines])

  useLayoutEffect(() => {
    const el = cardRef.current
    if (!el) return
    if (!sortHighlighted || sortHighlightToken == null) {
      el.classList.remove('product-card-sort-highlight')
      return
    }
    el.classList.remove('product-card-sort-highlight')
    void el.getBoundingClientRect()
    el.classList.add('product-card-sort-highlight')
  }, [sortHighlighted, sortHighlightToken])

  const handleIncreaseQty = () => {
    if (qty >= priceInfo.stock) {
      showErrorToast(stockLimitMessage(priceInfo.stock))
      return
    }
    setQty(qty + 1)
  }

  const handleAddToCart = () => {
    if (outOfStock) {
      showErrorToast('Out of stock')
      return
    }
    addItem(product, hasSizes ? size : undefined, qty)
    showSuccessToast('Added to cart')
  }

  const handleBuyNow = () => {
    if (outOfStock) {
      showErrorToast('Out of stock')
      return
    }
    startBuyNow(product, hasSizes ? size : undefined, qty)
    navigate('/checkout', {
      state: {
        buyNow: true,
        returnTo: `${location.pathname}${location.search}`,
      },
    })
  }

  const variantButtonClass = (active: boolean, compact = false, index = 0) =>
    cn(
      'product-card-variant',
      index % 2 === 0 ? 'product-card-corner-a' : 'product-card-corner-b',
      compact
        ? 'min-w-[2.75rem]'
        : 'flex-1 min-w-0 lg:py-1.5 lg:text-[11px] lg:flex-none lg:min-h-7 lg:px-2 lg:py-1 lg:text-[10px]',
      active && 'product-card-variant-active'
    )

  const wishlistHeartClass = wished
    ? 'fill-gold-600 text-gold-600'
    : 'text-current'

  const showMetaBadges =
    product.featured || product.royal === true || productHasAudience(product)

  const wishlistButton = (buttonClass?: string) => (
    <button
      type="button"
      onClick={() => toggle(product.id)}
      className={buttonClass}
      aria-label={wished ? 'Remove from wishlist' : 'Add to wishlist'}
    >
      <Heart className={cn('h-3.5 w-3.5 lg:h-4 lg:w-4', wishlistHeartClass)} />
    </button>
  )

  const mobileActionButtonClass = (
    variant: 'primary' | 'secondary' | 'buyNow' | 'wishlist',
    active = false,
    corner: 'a' | 'b' = 'a'
  ) =>
    cn(
      'product-card-action-btn',
      corner === 'a' ? 'product-card-corner-a' : 'product-card-corner-b',
      variant === 'primary' && 'product-card-action-btn--cart',
      variant === 'buyNow' && 'product-card-action-btn--buy',
      variant === 'wishlist' &&
        cn(
          'product-card-action-btn--wishlist',
          active && 'product-card-action-btn--wishlist-active'
        )
    )

  return (
    <div
      ref={cardRef}
      id={`product-card-${product.id}`}
      className={cn('group product-card scroll-mt-24', className)}
    >
      <div
        className={cn(
          'flex flex-row gap-3.5 lg:flex-col lg:gap-0 lg:p-0',
          showActions ? 'px-3.5 pt-3.5 pb-0.5' : 'p-3.5'
        )}
      >
        <Link
          to={`/shop/${product.id}`}
          state={productDetailLinkState(product)}
          className="shrink-0 lg:block"
        >
          <div
            className={cn(
              'product-card-media relative aspect-square w-[9rem] max-sm:bg-transparent md:w-[8rem] lg:w-full lg:aspect-square'
            )}
          >
            <AnimatePresence mode="sync" initial={false}>
              <motion.div
                key={displayImage}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.35, ease: storeEase }}
                className="absolute inset-0"
              >
                <StoreImage
                  src={displayImage}
                  alt={product.name}
                  priority={imagePriority}
                  containerClassName="h-full w-full"
                  className="transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                  fallback={
                    <span className="font-display text-2xl text-zinc-600">OK</span>
                  }
                />
              </motion.div>
            </AnimatePresence>

            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-luxury-950/35 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 lg:opacity-100 lg:from-luxury-950/25"
              aria-hidden
            />

            {outOfStock && (
              <span className="absolute inset-0 z-10 flex items-center justify-center bg-luxury-950/65 backdrop-blur-[2px] text-xs font-semibold uppercase tracking-wide text-white lg:text-sm">
                Out of Stock
              </span>
            )}

            {!showActions &&
              wishlistButton(
                cn(
                  'absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-white/90 text-zinc-600 shadow-md backdrop-blur-sm transition-all hover:border-gold-500/40 hover:bg-gold-50 hover:text-gold-600 lg:opacity-0 lg:group-hover:opacity-100',
                  wished && 'border-gold-500/35 bg-gold-50 text-gold-600 opacity-100'
                )
              )}
          </div>
        </Link>

        <div
          className={cn(
            'product-card-body justify-center gap-1 lg:gap-0 lg:p-3',
            showActions ? 'lg:pb-2' : 'lg:pb-3'
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <p className="product-card-category">{product.category}</p>
            {!showActions &&
              wishlistButton(
                cn(
                  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors lg:hidden',
                  wished
                    ? 'border-gold-500/35 bg-gold-50 text-gold-600'
                    : 'border-luxury-700/50 bg-white text-zinc-500 hover:border-gold-500/35 hover:bg-gold-50/60 hover:text-gold-600'
                )
              )}
          </div>

          {showMetaBadges && (
            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1 max-sm:gap-1 lg:gap-1.5">
              {product.royal === true && (
                <span className="product-card-royal-badge">
                  <Crown className="h-3 w-3 shrink-0" aria-hidden />
                  Royal
                </span>
              )}
              {product.featured && (
                <span className="product-card-featured-badge">
                  <Sparkles className="h-3 w-3 shrink-0" aria-hidden />
                  Featured
                </span>
              )}
              {productHasAudience(product) && (
                <ProductAudienceBadge
                  audience={product.audience}
                  placement="inline"
                />
              )}
            </div>
          )}

          <div className={cn('relative', showMetaBadges ? 'mt-1' : 'mt-0.5')}>
            <span
              ref={titleMeasureRef}
              aria-hidden
              className="pointer-events-none invisible absolute inset-x-0 top-0 block font-display text-[15px] font-semibold line-clamp-2"
            >
              {product.name}
            </span>
            <Link to={`/shop/${product.id}`} state={productDetailLinkState(product)}>
              <h3
                className={cn(
                  'product-card-title font-display text-[15px] font-semibold leading-snug text-zinc-900 line-clamp-2',
                  titleLines === 2
                    ? 'min-h-[2.5rem] lg:min-h-[2.125rem] lg:h-auto'
                    : 'min-h-[1.25rem] lg:h-auto'
                )}
              >
                {product.name}
              </h3>
            </Link>
          </div>

          {product.subtitle?.trim() ? (
            <p className="truncate text-[11px] leading-tight text-zinc-500 lg:text-xs">
              {product.subtitle.trim()}
            </p>
          ) : (
            <p className="hidden lg:block lg:invisible lg:h-4" aria-hidden>
              {'\u00A0'}
            </p>
          )}

          {product.rating != null && product.rating > 0 && (
            <StarRatingDisplay
              rating={product.rating}
              count={product.ratingCount}
              size="sm"
              className="mt-0.5"
            />
          )}

          <div className="-mt-1.5 flex flex-wrap items-end gap-2 lg:-mt-0.5">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="product-card-price">
                {formatCurrency(priceInfo.price)}
              </span>
              {priceInfo.hasDiscount && (
                <span className="text-xs text-zinc-400 line-through">
                  {formatCurrency(priceInfo.originalPrice)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {showActions && (
        <>
          <div className="product-card-actions product-card-actions-panel mx-3 mb-3 rounded-xl px-2.5 pt-1 pb-2.5 lg:hidden">
            <div className="mb-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
              <div className="product-card-qty-row">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                  Qty
                </span>
                <QuantityStepper
                  size="sm"
                  tone="light"
                  shape="product-card"
                  className="shrink-0"
                  value={qty}
                  min={1}
                  max={priceInfo.stock}
                  onDecrease={() => setQty(Math.max(1, qty - 1))}
                  onIncrease={handleIncreaseQty}
                />
              </div>

              {hasSizes ? (
                <div
                  className="flex flex-wrap items-center gap-1.5"
                  role="group"
                  aria-label="Variant"
                >
                  {productVariants(product).map((v, index) => (
                    <button
                      key={v.size}
                      type="button"
                      onClick={() => updateSize(v.size)}
                      className={variantButtonClass(size === v.size, true, index)}
                    >
                      {v.size}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-[auto_1fr_1fr] gap-2">
              {wishlistButton(
                cn(mobileActionButtonClass('wishlist', wished, 'a'), '!w-12 shrink-0')
              )}
              <button
                type="button"
                className={cn(
                  mobileActionButtonClass('primary', false, 'b'),
                  'px-2 text-[10px] font-semibold sm:text-xs'
                )}
                disabled={outOfStock}
                onClick={handleAddToCart}
              >
                Add to Cart
              </button>
              <button
                type="button"
                className={cn(
                  mobileActionButtonClass('buyNow', false, 'a'),
                  'px-2 text-[10px] font-semibold sm:text-xs'
                )}
                disabled={outOfStock}
                onClick={handleBuyNow}
              >
                Buy Now
              </button>
            </div>
          </div>

          <div className="product-card-actions product-card-actions--desktop hidden flex-1 flex-col gap-3 px-4 pb-4 pt-1.5 lg:flex lg:gap-2 lg:px-3 lg:pb-3 lg:pt-2">
            {hasSizes ? (
              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 lg:sr-only lg:mb-0">
                  Variant
                </p>
                <div className="flex flex-nowrap gap-1.5 lg:flex-wrap lg:gap-1">
                  {productVariants(product).map((v, index) => (
                    <button
                      key={v.size}
                      type="button"
                      onClick={() => updateSize(v.size)}
                      className={variantButtonClass(size === v.size, false, index)}
                    >
                      {v.size}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mt-auto flex flex-col gap-3 lg:mt-0 lg:flex-row lg:items-center lg:gap-2">
              <div className="product-card-qty-row lg:shrink-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 lg:sr-only">
                  Qty
                </p>
                <QuantityStepper
                  size="sm"
                  tone="light"
                  shape="product-card"
                  value={qty}
                  min={1}
                  max={priceInfo.stock}
                  onDecrease={() => setQty(Math.max(1, qty - 1))}
                  onIncrease={handleIncreaseQty}
                />
              </div>

              <div className="grid grid-cols-[auto_1fr_1fr] gap-2 lg:ml-auto lg:flex lg:flex-1 lg:justify-end lg:gap-1.5">
                {wishlistButton(
                  cn(
                    'product-card-action-btn product-card-action-btn--wishlist product-card-corner-a !w-12 shrink-0',
                    wished && 'product-card-action-btn--wishlist-active'
                  )
                )}
                <Button
                  size="sm"
                  className="product-card-corner-b w-full min-w-0 lg:px-3"
                  disabled={outOfStock}
                  onClick={handleAddToCart}
                >
                  Add to Cart
                </Button>
                <Button
                  size="sm"
                  variant="buyNow"
                  className="product-card-corner-a w-full min-w-0 lg:px-3"
                  disabled={outOfStock}
                  onClick={handleBuyNow}
                >
                  Buy Now
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
